import api from '../config/axiosConfig';

const CACHE_KEY = 'permissionsCache';
const CACHE_TTL_MS = 5 * 60 * 1000;

class PermissionService {
  constructor() {
    this.userPermissions = null;
    this.loading = false;
    this.inFlight = null; // { token, promise }
  }

  // Obtener permisos del usuario actual. La copia en caché pertenece a la sesión (token) que la
  // pidió: si cambia el usuario en la misma pestaña, no se reutiliza la del anterior.
  async getUserPermissions() {
    const token = sessionStorage.getItem('authToken');
    if (this.userPermissions && this.userPermissions.token === token && !this.isExpired()) {
      return this.userPermissions.data;
    }

    // Tras recargar la página (F5) la memoria se pierde: usar la copia de la sesión si sigue vigente
    const stored = this.readStoredCache(token);
    if (stored) {
      this.userPermissions = stored;
      return stored.data;
    }

    // Navbar, ProtectedRoute y usePermissions la piden al mismo tiempo: una sola petición para todos
    if (this.inFlight && this.inFlight.token === token) {
      return this.inFlight.promise;
    }
    const promise = this.fetchPermissions(token).finally(() => {
      if (this.inFlight?.promise === promise) this.inFlight = null;
    });
    this.inFlight = { token, promise };
    return promise;
  }

  // allowStale: sin señal se usa la última copia de esta sesión aunque haya pasado su vigencia
  // (en campo es mejor un menú de hace horas que uno vacío).
  readStoredCache(token, { allowStale = false } = {}) {
    try {
      const stored = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (stored && stored.token === token && (allowStale || Date.now() - stored.timestamp <= stored.expiresIn)) {
        return stored;
      }
    } catch {
      // copia corrupta: se ignora y se vuelve a pedir
    }
    return null;
  }

  async fetchPermissions(token) {
    try {
      this.loading = true;
      console.log('🔍 Obteniendo permisos del usuario actual...');
      // Sesión por pestaña (sessionStorage): cada pestaña puede tener su propio usuario
      const token = sessionStorage.getItem('authToken');
      
      console.log('🔍 Token disponible:', {
        'sessionStorage.authToken': token ? 'PRESENTE' : 'AUSENTE'
      });
      
      console.log('🚀 Llamando directamente a /users/current-permissions...');
      console.log('🔍 BaseURL configurada:', api.defaults.baseURL);
      const response = await api.get('/users/current-permissions');
      console.log('✅ Respuesta completa del servidor:', {
        status: response.status,
        data: response.data,
        screens: response.data?.screens?.length || 0,
        role: response.data?.role?.name || 'NO_ROLE'
      });
      
      // Validar estructura de respuesta
      if (!response.data || !response.data.screens) {
        console.warn('⚠️ Respuesta del servidor no tiene la estructura esperada:', response.data);
      }
      this.userPermissions = {
        token,
        data: response.data,
        timestamp: Date.now(),
        expiresIn: CACHE_TTL_MS
      };
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(this.userPermissions));
      } catch {
        // sin espacio en el almacenamiento: queda solo en memoria
      }
      
      // Guardar información del usuario en localStorage para uso en otros componentes
      if (response.data.userId) {
        const userInfo = {
          id: response.data.userId,
          userId: response.data.userId,
          username: response.data.username,
          email: response.data.email,
          firstName: response.data.firstName,
          firstLastName: response.data.firstLastName,
          role: response.data.role
        };
        sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
        console.log('✅ userInfo guardado en sessionStorage:', userInfo);
      }
      
      console.log('✅ Permisos del usuario cargados:', this.userPermissions.data);
      return this.userPermissions.data;
      
    } catch (error) {
      // Sin señal (no hubo respuesta): seguir con los últimos permisos conocidos de esta sesión
      if (!error.response) {
        const stale = this.readStoredCache(token, { allowStale: true });
        if (stale) {
          console.warn('Sin conexión: usando los últimos permisos guardados de esta sesión.');
          this.userPermissions = stale;
          return stale.data;
        }
      }
      console.error('❌ Error obteniendo permisos del usuario:', error);
      console.error('❌ Status:', error.response?.status);
      console.error('❌ Status Text:', error.response?.statusText);
      console.error('❌ URL:', error.config?.url);
      console.error('❌ Response Data:', error.response?.data);
      console.error('❌ Request Headers:', error.config?.headers);
      
      // Verificar si es un error de autenticación
      if (error.response?.status === 401) {
        console.error('🔐 Error de autenticación - Token inválido o expirado');
        // Limpiar token inválido
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('userInfo');
      } else if (error.response?.status === 403) {
        console.error('🔒 Error de permisos - Usuario sin permisos configurados');
        console.error('📋 Detalles del error:', error.response?.data);
        
        // Si es error de permisos no configurados, limpiar sesión y forzar logout
        if (error.response?.data?.error === 'PERMISOS_NO_CONFIGURADOS') {
          console.error('❌ PERMISOS NO CONFIGURADOS: El rol del usuario no tiene permisos en el sistema');
          console.error('🔧 SOLUCIÓN: El administrador debe configurar permisos para el rol');
          
          // Limpiar sesión
          sessionStorage.removeItem('authToken');
          sessionStorage.removeItem('userInfo');
          
          // Redirigir al login con mensaje de error
          window.location.href = '/login?error=permisos_no_configurados';
          return;
        }
      } else if (error.response?.status === 404) {
        console.error('🔍 Endpoint no encontrado - Verificar que el backend esté corriendo');
      } else if (error.response?.status === 500) {
        console.error('💥 Error interno del servidor');
        if (error.response?.data?.includes('Usuario no encontrado')) {
          console.error('👤 PROBLEMA: El usuario no existe en la base de datos');
          console.error('🔧 SOLUCIÓN: Ejecutar query para crear el usuario en la BD');
        }
      }
      
      // Si el endpoint no existe o hay error de autenticación, devolver permisos vacíos
      // pero no fallar completamente
      return { 
        screens: [], 
        role: { name: 'UNKNOWN', id: null },
        error: `${error.message} (Status: ${error.response?.status || 'No response'})`,
        errorDetails: {
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url
        }
      };
    } finally {
      this.loading = false;
    }
  }

  // Verificar si los permisos han expirado
  isExpired() {
    if (!this.userPermissions) return true;
    return Date.now() - this.userPermissions.timestamp > this.userPermissions.expiresIn;
  }

  // Verificar si el usuario tiene acceso a una pantalla específica
  async hasScreenAccess(route) {
    const permissions = await this.getUserPermissions();
    
    if (!permissions.screens) {
      console.log(`❌ Sin permisos de pantallas para verificar: ${route}`);
      return false;
    }

    // Buscar la pantalla por ruta
    const screenPermission = permissions.screens.find(screen => 
      screen.route === route || screen.route.includes(route) || route.includes(screen.route)
    );

    const hasAccess = screenPermission && screenPermission.canRead;
    
    console.log(`🔐 Acceso a ${route}: ${hasAccess ? '✅ PERMITIDO' : '❌ DENEGADO'}`, screenPermission);
    
    return hasAccess;
  }

  // Verificar si el usuario puede escribir en una pantalla
  async canWrite(route) {
    const permissions = await this.getUserPermissions();
    const screenPermission = permissions.screens?.find(screen => 
      screen.route === route || screen.route.includes(route) || route.includes(screen.route)
    );
    
    return screenPermission && screenPermission.canWrite;
  }

  // Verificar si el usuario puede eliminar en una pantalla
  async canDelete(route) {
    const permissions = await this.getUserPermissions();
    const screenPermission = permissions.screens?.find(screen => 
      screen.route === route || screen.route.includes(route) || route.includes(screen.route)
    );
    
    return screenPermission && screenPermission.canDelete;
  }

  // Verificar si el usuario es administrador de una pantalla
  async canAdmin(route) {
    const permissions = await this.getUserPermissions();
    const screenPermission = permissions.screens?.find(screen => 
      screen.route === route || screen.route.includes(route) || route.includes(screen.route)
    );
    
    return screenPermission && screenPermission.canAdmin;
  }

  // Obtener todas las rutas permitidas para el usuario
  async getAllowedRoutes() {
    const permissions = await this.getUserPermissions();
    
    if (!permissions.screens) return [];
    
    return permissions.screens
      .map(screen => screen.route);
  }

  // Limpiar caché de permisos (memoria, copia de la sesión y petición en curso)
  clearCache() {
    this.userPermissions = null;
    this.inFlight = null;
    try {
      sessionStorage.removeItem(CACHE_KEY);
    } catch {
      // nada que limpiar
    }
  }

  // Función para refrescar permisos después del login
  async refreshAfterLogin() {
    console.log('🔐 Refrescando permisos después del login...');
    try {
      // Limpiar cualquier cache previo
      this.clearCache();
      
      // Recargar permisos frescos desde el servidor
      const userPermissions = await this.getUserPermissions();
      console.log('✅ Permisos refrescados después del login:', {
        screens: userPermissions.screens?.length || 0,
        role: userPermissions.role?.name
      });
      
      return userPermissions;
    } catch (error) {
      console.error('❌ Error refrescando permisos después del login:', error);
      throw error;
    }
  }

  // Obtener información del rol del usuario
  async getUserRole() {
    const permissions = await this.getUserPermissions();
    return permissions.role;
  }
}
// Instancia singleton
const permissionService = new PermissionService();

export default permissionService;
